import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";

import { LOCALES, en, zh } from "../i18n";
import type { Locale, TranslationKey } from "../i18n";

const DICTIONARIES: Record<Locale, Record<string, string>> = { en, zh };
const STORAGE_KEY = "bo.locale";

export type Translate = (
  key: TranslationKey,
  vars?: Record<string, string | number>,
) => string;

interface LocaleValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: Translate;
}

const LocaleContext = createContext<LocaleValue | null>(null);

function storedLocale(): Locale {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (LOCALES.includes(value as Locale)) return value as Locale;
  } catch {
    /* private browsing — fall through to the browser's preference */
  }
  return navigator.language?.toLowerCase().startsWith("zh") ? "zh" : "en";
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(storedLocale);

  useEffect(() => {
    document.documentElement.lang = locale === "zh" ? "zh-Hans" : "en";
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* preference simply will not persist */
    }
  }, []);

  /**
   * Looks a key up and fills `{placeholders}`.
   *
   * Falls back to English, then to the key itself. Showing the raw key is
   * deliberate: it is obviously wrong in a screenshot, where an English string
   * sitting in Chinese copy reads as an oversight nobody reports.
   */
  const t = useCallback<Translate>(
    (key, vars) => {
      const template = DICTIONARIES[locale][key] ?? DICTIONARIES.en[key] ?? key;
      if (!vars) return template;

      return Object.entries(vars).reduce(
        (text, [name, value]) => text.replaceAll(`{${name}}`, String(value)),
        template,
      );
    },
    [locale],
  );

  const value = useMemo(
    () => ({ locale, setLocale, t }),
    [locale, setLocale, t],
  );
  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) throw new Error("useLocale must be used inside LocaleProvider");
  return context;
}

/** Shorthand for the common case of only needing the translate function. */
export function useT(): Translate {
  return useLocale().t;
}
